<template>
  <div class="container-fluid py-4">
    <div class="row">
      <!-- Left Column: Add Items -->
      <div class="col-md-4 mb-4">
        <div class="card">
          <div class="card-header pb-0">
            <h6>新增選單項目</h6>
          </div>
          <div class="card-body">
            <!-- System Pages -->
            <h6 class="text-uppercase text-body text-xs font-weight-bolder">
              系統頁面
            </h6>
            <div class="list-group mb-4">
              <button
                v-for="page in systemPages"
                :key="page.value"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                @click="addMenuItem('system_page', page.title, page.value)"
              >
                {{ page.title }}
                <i class="fas fa-plus text-xs"></i>
              </button>
            </div>

            <!-- Brands -->
            <h6
              class="text-uppercase text-body text-xs font-weight-bolder mt-4"
            >
              品牌
            </h6>
            <div class="mb-2">
              <input
                type="text"
                class="form-control"
                placeholder="搜尋品牌..."
                v-model="brandSearch"
              />
            </div>
            <div
              class="list-group mb-4"
              style="max-height: 200px; overflow-y: auto"
            >
              <button
                v-for="brand in filteredBrands"
                :key="brand.id"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                @click="addMenuItem('brand', brand.name, brand.id)"
              >
                {{ brand.name }}
                <i class="fas fa-plus text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Current Menu -->
      <div class="col-md-8">
        <div class="card">
          <div class="card-header pb-0 d-flex justify-content-between">
            <h6>目前主選單結構 (Main Menu)</h6>
            <div class="text-xs text-muted">
              拖曳項目以排序，向右拖曳可形成子選單 (尚未支援)
            </div>
            <button class="btn btn-primary btn-sm" @click="saveOrder">
              儲存排序
            </button>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">載入中...</div>
            <ul v-else class="list-group">
              <li
                v-for="(item, index) in menuItems"
                :key="item.id"
                class="list-group-item d-flex justify-content-between align-items-center draggable-item"
                draggable="true"
                @dragstart="dragStart(index, $event)"
                @dragover.prevent
                @dragenter="dragEnter(index)"
                @drop="dragDrop(index)"
                :class="{ 'dragging-over': dragOverIndex === index }"
              >
                <div class="d-flex align-items-center">
                  <div class="me-3 text-secondary cursor-move">
                    <i class="fas fa-bars"></i>
                  </div>
                  <div>
                    <h6 class="mb-0">{{ item.title }}</h6>
                    <small class="text-muted">{{
                      translateType(item.type)
                    }}</small>
                  </div>
                </div>
                <div>
                  <button
                    class="btn btn-link text-primary mb-0 px-2"
                    @click="editItem(item)"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button
                    class="btn btn-link text-danger mb-0 px-2"
                    @click="deleteItem(item.id)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </li>
              <li
                v-if="menuItems.length === 0"
                class="list-group-item text-center text-muted"
              >
                選單目前為空，請從左側新增項目
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cursor-move {
  cursor: move;
}
.draggable-item {
  transition: all 0.2s;
  border: 1px solid #dee2e6;
  margin-bottom: 5px;
}
.draggable-item.dragging-over {
  border: 2px dashed #28a745;
  background-color: #f8f9fa;
  transform: scale(1.02);
}
</style>

<script>
import request from "@/utils/request";
import Swal from "sweetalert2";

export default {
  name: "MenuManagement",
  data() {
    return {
      menuItems: [],
      brands: [],
      brandSearch: "",
      loading: false,
      systemPages: [
        { title: "首頁 (Home)", value: "/" },
        { title: "全部商品 (All Products)", value: "/products" },
        { title: "關於我們 (About)", value: "/about" },
        { title: "日誌 (Blog)", value: "/blog" },
        { title: "常見問題 (FAQ)", value: "/faq" },
        { title: "聯絡我們 (Contact)", value: "/contact" },
      ],
      dragStartIndex: null,
      dragOverIndex: null,
    };
  },
  computed: {
    filteredBrands() {
      if (!this.brandSearch) return this.brands;
      return this.brands.filter((b) =>
        b.name.toLowerCase().includes(this.brandSearch.toLowerCase())
      );
    },
  },
  methods: {
    async fetchMenu() {
      this.loading = true;
      try {
        const res = await request.get("/api/admin/menu-items");
        this.menuItems = res.data.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async fetchBrands() {
      try {
        const res = await request.get("/api/admin/brands");
        this.brands = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    translateType(type) {
      const map = {
        system_page: "系統頁面",
        brand: "品牌連結",
        custom_link: "自定義連結",
      };
      return map[type] || type;
    },
    async addMenuItem(type, title, value) {
      try {
        await request.post("/api/admin/menu-items", {
          title,
          type,
          value: String(value),
          order: this.menuItems.length,
          is_active: true,
        });
        await this.fetchMenu();

        // Show simplified toast
        const Toast = Swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
        });
        Toast.fire({
          icon: "success",
          title: "已新增",
        });
      } catch (e) {
        console.error(e);
        Swal.fire("錯誤", "新增失敗", "error");
      }
    },
    async editItem(item) {
      const { value: newTitle } = await Swal.fire({
        title: "編輯標題",
        input: "text",
        inputValue: item.title,
        showCancelButton: true,
        confirmButtonText: "儲存",
        cancelButtonText: "取消",
        inputValidator: (value) => {
          if (!value) {
            return "標題不能為空";
          }
        },
      });

      if (newTitle) {
        try {
          // Determine the correct API endpoint
          await request.put(`/api/admin/menu-items/${item.id}`, {
            title: newTitle,
            type: item.type, // Ensure type is preserved
            value: item.value, // Ensure value is preserved
            is_active: item.is_active,
          });

          await this.fetchMenu();

          const Toast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
          });
          Toast.fire({
            icon: "success",
            title: "已更新",
          });
        } catch (e) {
          console.error(e);
          Swal.fire("錯誤", "更新失敗", "error");
        }
      }
    },
    async deleteItem(id) {
      Swal.fire({
        title: "確定要刪除此選單項目嗎？",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ea0606",
        confirmButtonText: "確定刪除",
        cancelButtonText: "取消",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await request.delete(`/api/admin/menu-items/${id}`);
            await this.fetchMenu();
            Swal.fire("已刪除", "項目已刪除", "success");
          } catch (e) {
            console.error(e);
            Swal.fire("錯誤", "刪除失敗", "error");
          }
        }
      });
    },
    moveItem(index, direction) {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= this.menuItems.length) return;

      // Swap locally
      const items = [...this.menuItems];
      const temp = items[index];
      items[index] = items[newIndex];
      items[newIndex] = temp;
      this.menuItems = items;
    },
    async saveOrder() {
      try {
        await request.post("/api/admin/menu/reorder", {
          items: this.menuItems.map((item) => ({
            id: item.id,
            children: [], // Flattened for now
          })),
        });
        Swal.fire("成功", "排序已更新", "success");
      } catch (e) {
        console.error(e);
        Swal.fire("錯誤", "排序更新失敗", "error");
      }
    },
    // Drag and Drop Methods
    dragStart(index, event) {
      this.dragStartIndex = index;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", index);
      // Add ghost effect if needed
    },
    dragEnter(index) {
      if (index !== this.dragStartIndex) {
        this.dragOverIndex = index;
      }
    },
    dragDrop(index) {
      this.dragOverIndex = null;
      if (this.dragStartIndex === null || this.dragStartIndex === index) return;

      // Reorder array
      const items = [...this.menuItems];
      const draggedItem = items.splice(this.dragStartIndex, 1)[0];
      items.splice(index, 0, draggedItem);

      this.menuItems = items;
      this.dragStartIndex = null;
    },
  },
  mounted() {
    this.fetchMenu();
    this.fetchBrands();
  },
};
</script>
